import { Slot } from '@radix-ui/react-slot';
import { cx } from '@repo/themes/styled-system/css';
import { table, TableVariant } from '@repo/themes/styled-system/recipes';
import { ComponentProps } from 'react';

export interface TableProps
  extends Partial<TableVariant>,
    ComponentProps<'table'> {
  asChild?: boolean;
}

const Table = ({ children, className, asChild, ...allProps }: TableProps) => {
  const [tableProps, props] = table.splitVariantProps(allProps);
  const classes = table(tableProps);

  const Comp = asChild ? Slot : 'table';

  return (
    <div className={cx(classes.container, className)}>
      <Comp className={classes.table} {...props}>
        {children}
      </Comp>
    </div>
  );
};

export interface TableBodyProps
  extends Partial<TableVariant>,
    ComponentProps<'tbody'> {
  asChild?: boolean;
}

const TableBody = ({ children, asChild, ...allProps }: TableBodyProps) => {
  const [bodyProps, props] = table.splitVariantProps(allProps);
  const classes = table(bodyProps);

  const Comp = asChild ? Slot : 'tbody';

  return (
    <Comp {...props} className={classes.body}>
      {children}
    </Comp>
  );
};

export interface TableHeaderProps
  extends Partial<TableVariant>,
    ComponentProps<'thead'> {
  asChild?: boolean;
}

const TableHeader = ({ children, asChild, ...allProps }: TableHeaderProps) => {
  const [headerProps, props] = table.splitVariantProps(allProps);
  const classes = table(headerProps);

  const Comp = asChild ? Slot : 'thead';

  return (
    <Comp className={classes.header} {...props}>
      {children}
    </Comp>
  );
};

export interface TableHeadProps
  extends Partial<TableVariant>,
    ComponentProps<'th'> {
  asChild?: boolean;
}

const TableHead = ({
  children,
  className,
  asChild,
  ...allProps
}: TableHeadProps) => {
  const [headProps, props] = table.splitVariantProps(allProps);
  const classes = table(headProps);

  const Comp = asChild ? Slot : 'th';

  return (
    <Comp className={cx(classes.head, className)} {...props}>
      {children}
    </Comp>
  );
};

export interface TableRowProps
  extends Partial<TableVariant>,
    ComponentProps<'tr'> {
  asChild?: boolean;
}

const TableRow = ({ children, asChild, ...allProps }: TableRowProps) => {
  const [rowProps, props] = table.splitVariantProps(allProps);
  const classes = table(rowProps);

  const Comp = asChild ? Slot : 'tr';

  return (
    <Comp className={classes.row} {...props}>
      {children}
    </Comp>
  );
};

export interface TableCellProps
  extends Partial<TableVariant>,
    ComponentProps<'td'> {
  asChild?: boolean;
}

const TableCell = ({
  children,
  className,
  asChild,
  ...allProps
}: TableCellProps) => {
  const [cellProps, props] = table.splitVariantProps(allProps);
  const classes = table(cellProps);

  const Comp = asChild ? Slot : 'td';

  return (
    <Comp className={cx(classes.cell, className)} {...props}>
      {children}
    </Comp>
  );
};

TableBody.displayName = 'Table.Body';
TableHeader.displayName = 'Table.Header';
TableHead.displayName = 'Table.Head';
TableRow.displayName = 'Table.Row';
TableCell.displayName = 'Table.Cell';

export default {
  Table,
  Body: TableBody,
  Header: TableHeader,
  Head: TableHead,
  Row: TableRow,
  Cell: TableCell,
};
